import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FileTextIcon, SparklesIcon } from "lucide-react";
import { useActiveOrgId } from "../../state/orgs";
import { usePolicies } from "../../state/policies";
import { useOrganizationPeople } from "../../state/governance";
import { useConnections } from "../../state/connections";
import { MentionAvatar } from "./UserMention";
import {
  reviewMentionOptions,
  type ReviewMentionKind,
  type ReviewMentionOption,
} from "./reviewMentions";
import { cn } from "~/lib/utils";

export type MentionKeyHandler = (key: string, event: KeyboardEvent) => boolean;
export function ReviewMentionPicker({
  kind,
  query,
  onSelect,
  onDismiss,
  keyHandler,
}: {
  kind: ReviewMentionKind;
  query: string;
  onSelect: (option: ReviewMentionOption) => void;
  onDismiss: () => void;
  keyHandler: React.RefObject<MentionKeyHandler | null>;
}) {
  const org = useActiveOrgId();
  const policies = usePolicies(kind === "document" ? org : null);
  const people = useOrganizationPeople(kind === "person" ? org : null);
  const connections = useConnections();
  const options = useMemo(
    () =>
      reviewMentionOptions(kind, query, {
        policies: policies.snapshot?.policies ?? [],
        people: people.snapshot?.people ?? [],
        skills: connections.skills,
      }),
    [kind, query, policies.snapshot, people.snapshot, connections.skills],
  );
  const [selection, setSelection] = useState(0);
  const active = Math.min(selection, Math.max(0, options.length - 1));
  const list = useRef<HTMLDivElement>(null);
  const id = useId();
  const loading =
    kind === "document"
      ? policies.isPending
      : kind === "person"
        ? people.isPending
        : connections.isPending;
  const unavailable =
    kind === "document"
      ? policies.unavailableReason
      : kind === "person"
        ? people.unavailableReason
        : connections.error || !connections.available;
  const title = kind === "document" ? "Policies & files" : kind === "person" ? "People" : "Skills";
  useEffect(() => setSelection(0), [kind, query]);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active]);
  useEffect(() => {
    keyHandler.current = (key, event) => {
      if (event.isComposing) return false;
      if (key === "Escape") {
        event.preventDefault();
        onDismiss();
        return true;
      }
      if (!["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(key)) return false;
      if (key === "Tab" && !options.length) {
        onDismiss();
        return false;
      }
      event.preventDefault();
      if (key === "ArrowDown" || key === "ArrowUp") {
        setSelection(
          (active + (key === "ArrowDown" ? 1 : -1) + options.length) % Math.max(1, options.length),
        );
      } else if (options[active]) onSelect(options[active]);
      return true;
    };
    return () => {
      keyHandler.current = null;
    };
  }, [active, options, onSelect, onDismiss, keyHandler]);
  return (
    <div
      className="absolute inset-x-0 bottom-full z-30 mb-2 overflow-hidden rounded-2xl border border-border/80 bg-popover text-popover-foreground shadow-xl"
      data-review-mention-picker="true"
      onPointerDown={(event) => event.preventDefault()}
    >
      <div className="flex items-center justify-between border-b border-border/60 px-3 py-2 text-xs text-muted-foreground">
        <span>{title}</span>
        <span>↑ ↓ select · Enter insert · Esc close</span>
      </div>
      <div
        ref={list}
        role="listbox"
        aria-label={title}
        aria-activedescendant={options[active] ? `${id}-${active}` : undefined}
        className="max-h-[min(16rem,40dvh)] overflow-y-auto overscroll-contain p-1.5"
      >
        {options.map((option, index) => (
          <button
            key={option.id}
            id={`${id}-${index}`}
            type="button"
            role="option"
            aria-selected={active === index}
            tabIndex={-1}
            onPointerMove={() => setSelection(index)}
            onClick={() => onSelect(option)}
            className={cn(
              "flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left",
              active === index && "bg-accent text-accent-foreground",
            )}
          >
            {option.person ? (
              <MentionAvatar person={option.person} />
            ) : option.kind === "skill" ? (
              <SparklesIcon className="size-4 shrink-0 text-violet-500" />
            ) : (
              <FileTextIcon className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{option.label}</span>
              <span className="block truncate text-xs text-muted-foreground">{option.detail}</span>
            </span>
          </button>
        ))}
        {!options.length ? (
          <p role="status" className="px-3 py-4 text-sm text-muted-foreground">
            {loading
              ? `Loading ${title.toLowerCase()}…`
              : unavailable
                ? `${title} could not be loaded.`
                : query
                  ? `No matches for “${query}”.`
                  : `No ${title.toLowerCase()} available.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}
